/**
 * useClubhouseState - Custom hook for Clubhouse admin module state management
 * Extracted from Admin.jsx to reduce state complexity
 * 
 * No changes to behavior, API endpoints, or request payloads.
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../utils/apiUrl';

const getAuthHeaders = () => {
  const token = sessionStorage.getItem('golfnuts_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const initialDeleteMemberModal = {
  show: false,
  step: 1, // 1: Initial confirm, 2: Type DELETE, 3: Final checkbox, 4: Success
  member: null,
  deleteInput: '',
  finalConfirmChecked: false,
  reason: '',
  loading: false,
  error: null,
  deletedEmail: null // Store deleted email for success message
};

export const useClubhouseState = ({ setError, setSuccess }) => {
  // Core clubhouse state
  const [clubhouseMembers, setClubhouseMembers] = useState([]);
  const [loadingClubhouse, setLoadingClubhouse] = useState(false);
  const [clubhouseView, setClubhouseView] = useState('members'); // 'members', 'leaderboards', 'winners'
  const [monthlyLeaderboard, setMonthlyLeaderboard] = useState([]);
  const [quarterlyLeaderboard, setQuarterlyLeaderboard] = useState([]);
  const [winners, setWinners] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);

  // Filter state
  const [memberSubscriptionFilter, setMemberSubscriptionFilter] = useState('all'); // all, subscribed, unsubscribed
  const [memberStatusFilter, setMemberStatusFilter] = useState('active'); // active, deleted

  // Delete member modal state
  const [deleteMemberModal, setDeleteMemberModal] = useState({ ...initialDeleteMemberModal });

  // Fetch clubhouse data based on current view
  const fetchClubhouseData = useCallback(async () => {
    setLoadingClubhouse(true);
    setError(null);
    try {
      if (clubhouseView === 'members') {
        const params = memberStatusFilter === 'deleted' ? '?include_deleted=true' : '';
        const response = await axios.get(`${API_URL}/api/clubhouse/admin/members${params}`);
        setClubhouseMembers(response.data || []);
      } else if (clubhouseView === 'leaderboards') {
        const [monthlyRes, quarterlyRes] = await Promise.all([
          axios.get(`${API_URL}/api/clubhouse/handicap/leaderboard/monthly-lowest`),
          axios.get(`${API_URL}/api/clubhouse/handicap/leaderboard/quarterly-most-improved`)
        ]);
        setMonthlyLeaderboard(monthlyRes.data.leaderboard || []);
        setQuarterlyLeaderboard(quarterlyRes.data.leaderboard || []);
      } else if (clubhouseView === 'winners') {
        const response = await axios.get(`${API_URL}/api/clubhouse/admin/winners/export`);
        setWinners(response.data?.winners || []);
      }
      
      // Always fetch scheduler status
      const schedulerRes = await axios.get(`${API_URL}/api/clubhouse/admin/scheduler/status`);
      setSchedulerStatus(schedulerRes.data);
    } catch (err) {
      setError('Failed to fetch clubhouse data: ' + err.message);
    } finally {
      setLoadingClubhouse(false);
    }
  }, [clubhouseView, memberStatusFilter, setError]);

  // Open delete member modal
  const openDeleteMemberModal = useCallback((member) => {
    setDeleteMemberModal({
      show: true,
      step: 1,
      member: member,
      deleteInput: '',
      finalConfirmChecked: false,
      reason: '',
      loading: false,
      error: null,
      deletedEmail: null
    });
  }, []);

  // Close delete member modal
  const closeDeleteMemberModal = useCallback(() => {
    setDeleteMemberModal({ ...initialDeleteMemberModal });
  }, []);

  // Handle delete member step navigation
  const handleDeleteMemberStep = useCallback((nextStep) => {
    setDeleteMemberModal(prev => ({ ...prev, step: nextStep }));
  }, []);

  // Confirm and execute member deletion
  const confirmDeleteMember = useCallback(async () => {
    if (!deleteMemberModal.member) return;
    
    // Prevent double-submission
    if (deleteMemberModal.loading) return;
    
    // Clear any previous error and set loading
    setDeleteMemberModal(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const headers = getAuthHeaders();
      const params = deleteMemberModal.reason ? `?reason=${encodeURIComponent(deleteMemberModal.reason)}` : '';
      const memberEmail = deleteMemberModal.member.email;
      const memberId = deleteMemberModal.member.user_id;
      
      await axios.delete(
        `${API_URL}/api/clubhouse/admin/members/${memberId}${params}`,
        { headers }
      );
      
      // Remove member from list immediately
      setClubhouseMembers(prev => prev.filter(m => m.user_id !== memberId));
      
      // Show success state (step 4)
      setDeleteMemberModal(prev => ({ 
        ...prev, 
        loading: false,
        step: 4,
        deletedEmail: memberEmail,
        error: null
      }));
      
      // Auto-close after 2 seconds
      setTimeout(() => {
        setDeleteMemberModal({ ...initialDeleteMemberModal });
      }, 2000);
      
    } catch (err) {
      // Handle different error types
      let errorMessage = 'Failed to delete user. Please try again.';
      
      if (err.response) {
        // Server responded with error
        errorMessage = err.response?.data?.detail || err.response?.data?.message || errorMessage;
      } else if (err.request) {
        // Network error
        errorMessage = "Couldn't connect to server. Please check your connection and try again.";
      } else if (err.code === 'ECONNABORTED') {
        // Timeout
        errorMessage = "Request timed out. Please try again.";
      }
      
      setDeleteMemberModal(prev => ({ 
        ...prev, 
        loading: false,
        error: errorMessage
      }));
    }
  }, [deleteMemberModal.member, deleteMemberModal.loading, deleteMemberModal.reason]);

  // Export clubhouse members to CSV
  const exportClubhouseMembers = useCallback(() => {
    const csv = [
      ['Email', 'Handicap', 'Joined Date', 'Profile Photo', 'Email Subscribed', 'Unsubscribed At', 'Eligible (Monthly)', 'Eligible (Quarterly)'].join(','),
      ...clubhouseMembers.map(m => [
        m.email,
        m.current_handicap || 'N/A',
        new Date(m.created_at).toLocaleDateString(),
        m.profile_photo_url ? 'Yes' : 'No',
        m.email_subscribed !== false ? 'Yes' : 'No',
        m.email_unsubscribed_at ? new Date(m.email_unsubscribed_at).toLocaleDateString() : '',
        m.eligibility?.is_eligible_for_monthly_lowest_hdcp_contest ? 'Yes' : 'No',
        m.eligibility?.is_eligible_for_quarterly_improvement_contest ? 'Yes' : 'No'
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clubhouse-members-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  }, [clubhouseMembers]);

  // Export winners to CSV
  const exportWinners = useCallback(() => {
    const csv = [
      ['Email', 'Contest Type', 'Period', 'Handicap', 'Shipping Name', 'Shipping Address', 'Phone'].join(','),
      ...winners.map(w => [
        w.email,
        w.contest_type,
        w.period,
        w.handicap || 'N/A',
        w.shipping_name || 'N/A',
        w.shipping_address || 'N/A',
        w.phone || 'N/A'
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clubhouse-winners-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  }, [winners]);

  return {
    // State
    clubhouseMembers,
    loadingClubhouse,
    clubhouseView,
    monthlyLeaderboard,
    quarterlyLeaderboard,
    winners,
    schedulerStatus,
    memberSubscriptionFilter,
    memberStatusFilter,
    deleteMemberModal,

    // Setters
    setClubhouseMembers,
    setClubhouseView,
    setMemberSubscriptionFilter,
    setMemberStatusFilter,
    setDeleteMemberModal,

    // Actions
    fetchClubhouseData,
    openDeleteMemberModal,
    closeDeleteMemberModal,
    handleDeleteMemberStep,
    confirmDeleteMember,
    exportClubhouseMembers,
    exportWinners,
  };
};

export default useClubhouseState;
